import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import API, { formatPrice, getImageUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Minus, Plus, ShoppingBag, Zap, Clock, Package, Heart, Trash2, ClipboardList, ChevronDown, ChevronUp, Copy, ChevronLeft, ChevronRight } from "lucide-react";

const formatPoCloseWib = (value) => {
  if (!value) return "";
  const text = String(value).trim();
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(text) ? `${text}T23:59` : text;
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (!match) return text;
  const [, y, m, d, hh, mm] = match;
  return `${d}/${m}/${y} ${hh}:${mm} WIB`;
};

const formatPoCloseShare = (value) => {
  if (!value) return "";
  const text = String(value).trim();
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(text) ? `${text}T23:59` : text;
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (!match) return text.toUpperCase();
  const [, , m, d, hh, mm] = match;
  const monthNames = ["JANUARI", "FEBRUARI", "MARET", "APRIL", "MEI", "JUNI", "JULI", "AGUSTUS", "SEPTEMBER", "OKTOBER", "NOVEMBER", "DESEMBER"];
  return `${Number(d)} ${monthNames[Number(m) - 1] || m} JAM ${hh}:${mm} WIB`;
};

const formatDiscountShare = (value, type = "percent") => {
  const amount = Number(value || 0);
  if (!amount) return "";
  if (type === "fixed") return formatPrice(amount);
  return `${amount % 1 === 0 ? Math.round(amount) : amount}%`;
};

const getDiscountShareValue = (product, brand, role) => {
  const productKey = role === "agen" ? "discount_agen" : `discount_${role}`;
  const brandKey = role === "agen" ? "discount_agent" : `discount_${role}`;
  const productValue = Number(product?.[`${productKey}_value`] || product?.[productKey] || 0);
  if (productValue > 0) {
    return formatDiscountShare(productValue, product?.[`${productKey}_type`] || "percent");
  }
  const brandValue = Number(brand?.[`${brandKey}_value`] || brand?.[brandKey] || 0);
  if (brandValue > 0) {
    return formatDiscountShare(brandValue, brand?.[`${brandKey}_type`] || "percent");
  }
  return "";
};

const buildSizeAdjustmentShareLines = (product) => {
  const rows = [];
  const seen = new Set();
  (product?.variants || []).forEach((variant) => {
    const adjustment = Number(variant?.size_price_adjustment || 0);
    if (adjustment <= 0) return;
    const label = String(variant?.size || variant?.color || variant?.style || "Size khusus").trim();
    const key = `${label}|||${adjustment}`;
    if (seen.has(key)) return;
    seen.add(key);
    rows.push(`${label} +${formatPrice(adjustment)}`);
  });
  return rows;
};

const buildProductShareText = (product, brandLabel) => {
  const brand = product?.brand || {};
  const title = String(product?.name || "Produk").toUpperCase().includes(" BY ")
    ? String(product?.name || "Produk").toUpperCase()
    : `${String(product?.name || "Produk").toUpperCase()}${brandLabel ? ` BY ${String(brandLabel).toUpperCase()}` : ""}`;
  const discounts = [
    ["AGEN", getDiscountShareValue(product, brand, "agen")],
    ["RESELLER", getDiscountShareValue(product, brand, "reseller")],
  ].filter(([, value]) => value);
  const lines = [`*📝 ${title}*`];
  if (discounts.length) lines.push(`▪️DISKON: *${discounts.map(([label, value]) => `${label} ${value}`).join(" | ")}*`);
  if (product?.preorder_closing_date) lines.push(`▪️BATAS MAX ISI LINK : *${formatPoCloseShare(product.preorder_closing_date)}*`);
  if (product?.dp_percentage) lines.push(`▪️DP 1 *${Number(product.dp_percentage)}%*`);
  if (product?.preorder_delivery_estimate) lines.push(`▪️ESTIMASI READY PUSAT *${String(product.preorder_delivery_estimate).toUpperCase()}*`);
  const sizeAdjustmentLines = buildSizeAdjustmentShareLines(product);
  if (sizeAdjustmentLines.length) lines.push("", "*TAMBAHAN SIZE:*", ...sizeAdjustmentLines);
  const orderUrl = `${window.location.origin}/products/${product?.id || ""}`;
  lines.push("", "*LINK ORDER👇🏻*", orderUrl);
  if (brand?.central_telegram_url) lines.push("", "*LINK KATALOG👇🏻*", brand.central_telegram_url);
  lines.push("", "*Isi Perlahan dan Teliti yaa, Happy Closing💖*");
  return lines.join("\n");
};

const uniqueValues = (items = [], mapper) => [...new Set((items || []).map(mapper).filter(Boolean))];

const getDefaultVariantSelection = (product) => {
  const variants = product?.variants || [];
  const styles = uniqueValues(variants, (v) => v.style);
  const hasStyles = styles.length > 0;
  const style = hasStyles ? styles[0] : "";
  const colors = hasStyles
    ? uniqueValues(variants.filter((v) => v.style === style), (v) => v.color)
    : uniqueValues(variants, (v) => v.color);
  const color = colors[0] || "";
  const sizes = hasStyles
    ? uniqueValues(variants.filter((v) => v.style === style && v.color === color), (v) => v.size)
    : uniqueValues(variants.filter((v) => v.color === color), (v) => v.size);
  return { style, color, size: sizes[0] || "" };
};

const getDefaultColorSizeForStyle = (product, style) => {
  const variants = product?.variants || [];
  const hasStyles = uniqueValues(variants, (v) => v.style).length > 0;
  const colors = hasStyles
    ? uniqueValues(variants.filter((v) => v.style === style), (v) => v.color)
    : uniqueValues(variants, (v) => v.color);
  const color = colors[0] || "";
  const sizes = hasStyles
    ? uniqueValues(variants.filter((v) => v.style === style && v.color === color), (v) => v.size)
    : uniqueValues(variants.filter((v) => v.color === color), (v) => v.size);
  return { color, size: sizes[0] || "" };
};

const getDefaultSizeForColor = (product, style, color) => {
  const variants = product?.variants || [];
  const hasStyles = uniqueValues(variants, (v) => v.style).length > 0;
  const sizes = hasStyles
    ? uniqueValues(variants.filter((v) => v.style === style && v.color === color), (v) => v.size)
    : uniqueValues(variants.filter((v) => v.color === color), (v) => v.size);
  return sizes[0] || "";
};

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart, fetchCart } = useCart();
  const { t, locale } = useLanguage();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const imageTouchStartRef = useRef(null);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedStyle, setSelectedStyle] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);
  // ── PO Cart (sessionStorage, per product) ──
  const PO_STORAGE_KEY = `po_cart_${id}`;
  const loadPOCart = () => {
    try { return JSON.parse(sessionStorage.getItem(PO_STORAGE_KEY) || "[]"); } catch { return []; }
  };
  const [poCart, setPoCart] = useState(loadPOCart);
  const [showPOCart, setShowPOCart] = useState(false);

  // Keep sessionStorage in sync
  useEffect(() => {
    sessionStorage.setItem(PO_STORAGE_KEY, JSON.stringify(poCart));
    if (poCart.length > 0) setShowPOCart(true);
  }, [poCart, PO_STORAGE_KEY]);

  const isReseller = ["reseller", "agen", "mitra", "produsen", "distributor", "admin"].includes(user?.role);

  useEffect(() => {
    API.get(`/products/${id}`).then((r) => {
      const productData = r.data;
      const defaultSelection = getDefaultVariantSelection(productData);
      setProduct(productData);
      setSelectedStyle(defaultSelection.style);
      setSelectedColor(defaultSelection.color);
      setSelectedSize(defaultSelection.size);
      setQuantity(1);
    }).catch(() => navigate("/products")).finally(() => setLoading(false));
    if (user) {
      API.get("/wishlist/ids").then((r) => setIsWishlisted(r.data.product_ids?.includes(id))).catch(() => { });
    }
  }, [id, navigate, user]);

  // 3-level variant hierarchy: Style → Color → Size
  const styles = [...new Set(product?.variants?.map((v) => v.style).filter(Boolean) || [])];
  const hasStyles = styles.length > 0;
  const colorsForStyle = hasStyles
    ? [...new Set(product?.variants?.filter(v => v.style === selectedStyle).map(v => v.color).filter(Boolean) || [])]
    : [...new Set(product?.variants?.map((v) => v.color) || [])];
  const sizesForStyleColor = hasStyles
    ? [...new Set(product?.variants?.filter(v => v.style === selectedStyle && v.color === selectedColor).map(v => v.size).filter(Boolean) || [])]
    : [...new Set(product?.variants?.filter(v => v.color === selectedColor).map(v => v.size).filter(Boolean) || [])];
  const sizes = sizesForStyleColor;
  const colors = colorsForStyle;
  const selectedVariant = product?.variants?.find((v) => 
    v.size === selectedSize && v.color === selectedColor && (!hasStyles || v.style === selectedStyle)
  );
  const requiresStyle = hasStyles;
  const requiresColor = colors.length > 0 || (product?.variants || []).some(v => Boolean(v?.color));
  const requiresSize = sizes.length > 0;
  const isVariantSelectionComplete = (!requiresStyle || Boolean(selectedStyle)) && (!requiresColor || Boolean(selectedColor)) && (!requiresSize || Boolean(selectedSize));

  const displayPricing = selectedVariant?.pricing || product?.pricing || { final_price: product?.price || 0, base_price: product?.price || 0, discount_pct: 0, discount_type: "percent", discount_value: 0, savings: 0, size_price_adjustment: 0, discounted_base_price: product?.price || 0 };
  const displaySizeAdjustment = Number(displayPricing.size_price_adjustment || selectedVariant?.size_price_adjustment || 0);
  const displayDiscountPct = Number(displayPricing.discount_pct || displayPricing.campaign?.discount_pct || 0);
  const displayDiscountLabel = displayDiscountPct > 0
    ? `-${displayDiscountPct % 1 === 0 ? displayDiscountPct.toFixed(0) : displayDiscountPct.toFixed(2)}%`
    : (displayPricing.discount_type === "fixed" ? `-${formatPrice(displayPricing.discount_value)}` : "");
  const brandLabel = product?.brand_name || product?.brand?.name || product?.brand_id?.replace("brand_", "") || "";
  const productImages = product?.images || [];
  const imageCount = productImages.length;
  const mainImageSrc = productImages?.[selectedImage]
    ? getImageUrl(productImages[selectedImage])
    : (productImages?.[0] ? getImageUrl(productImages[0]) : getImageUrl("naura-numa/assets/frontend/product_fallback.jpg"));

  const showPreviousImage = () => {
    if (imageCount <= 1) return;
    setSelectedImage((current) => (current - 1 + imageCount) % imageCount);
  };

  const showNextImage = () => {
    if (imageCount <= 1) return;
    setSelectedImage((current) => (current + 1) % imageCount);
  };

  const handleImageTouchStart = (event) => {
    if (imageCount <= 1) return;
    const touch = event.touches?.[0];
    if (!touch) return;
    imageTouchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleImageTouchEnd = (event) => {
    if (imageCount <= 1 || !imageTouchStartRef.current) return;
    const touch = event.changedTouches?.[0];
    if (!touch) return;
    const deltaX = touch.clientX - imageTouchStartRef.current.x;
    const deltaY = touch.clientY - imageTouchStartRef.current.y;
    imageTouchStartRef.current = null;
    if (Math.abs(deltaX) < 45 || Math.abs(deltaX) < Math.abs(deltaY) * 1.2) return;
    if (deltaX < 0) showNextImage();
    else showPreviousImage();
  };

  const getPricingForVariant = (style, color, size) => {
    const variant = product?.variants?.find(v => 
      (!hasStyles || v.style === style) && v.color === color && v.size === size
    );
    return variant?.pricing || product?.pricing || { final_price: product?.price || 0, base_price: product?.price || 0, discount_pct: 0, discount_type: "percent", discount_value: 0, savings: 0 };
  };

  const handleAddToCart = async () => {
    if (!user) { navigate("/login"); return; }
    if (!isVariantSelectionComplete) {
      toast.error(locale === "id" ? "Pilih model, warna, dan ukuran terlebih dahulu" : "Please select style, color, and size first");
      return;
    }
    if (product.stock_type === "preorder") {
      // PO products → add to local PO cart instead
      handleAddToPOCart();
      return;
    }
    setAdding(true);
    try {
      await addToCart(product.id, { variant_id: selectedVariant?.variant_id, style: selectedStyle || undefined, size: selectedSize, color: selectedColor }, quantity);
      await fetchCart();
      toast.success(locale === "id" ? "Berhasil ditambahkan ke keranjang!" : "Added to cart!");
    } catch { toast.error(locale === "id" ? "Gagal menambahkan ke keranjang" : "Failed to add to cart"); }
    finally { setAdding(false); }
  };

  const handleAddToPOCart = () => {
    const sizeLabel = selectedSize || "";
    const colorLabel = selectedColor || "";
    const styleLabel = selectedStyle || "";
    const key = `${styleLabel}||${sizeLabel}||${colorLabel}`;
    setPoCart(prev => {
      const existing = prev.find(i => `${i.style || ""}||${i.size}||${i.color}` === key);
      if (existing) {
        const updated = prev.map(i =>
          `${i.style || ""}||${i.size}||${i.color}` === key ? { ...i, quantity: i.quantity + quantity } : i
        );
        toast.success(locale === "id" ? "Jumlah diperbarui di list" : "Quantity updated in list");
        return updated;
      }
      toast.success(locale === "id" ? "Ditambahkan ke list!" : "Added to list!");
      return [...prev, { id: crypto.randomUUID(), variant_id: selectedVariant?.variant_id, style: styleLabel, size: sizeLabel, color: colorLabel, quantity }];
    });
  };

  const handleRemovePOItem = (itemId) => {
    setPoCart(prev => prev.filter(i => i.id !== itemId));
  };

  const handleUpdatePOQty = (itemId, newQty) => {
    if (newQty < 1) { handleRemovePOItem(itemId); return; }
    setPoCart(prev => prev.map(i => i.id === itemId ? { ...i, quantity: newQty } : i));
  };

  const handlePOCheckout = () => {
    if (!user) { navigate("/login"); return; }
    if (poCart.length === 0) { toast.error(locale === "id" ? "List kosong" : "List is empty"); return; }
    
    // We send total aggregated pricing data for PO cart
    navigate("/checkout", {
      state: {
        po_cart: true,
        product_id: product.id,
        product_name: product.name,
        brand_name: product.brand_name || product.brand?.name,
        product_image: product.images?.[0] || "",
        stock_type: product.stock_type,
        preorder_closing_date: product.preorder_closing_date,
        preorder_delivery_estimate: product.preorder_delivery_estimate,
        variants: product.variants || [],
        weight_gram: product.weight_gram || product.weight || product.berat || "",
        items: poCart.map(i => {
          const pricing = getPricingForVariant(i.style, i.color, i.size);
          return { 
            variant: { variant_id: i.variant_id, style: i.style || undefined, size: i.size, color: i.color, size_price_adjustment: pricing.size_price_adjustment || 0 },
            quantity: i.quantity,
            price: pricing.final_price,
            savings: pricing.savings
          };
        }),
      }
    });
  };

  const handleBuyNow = () => {
    if (!user) { navigate("/login"); return; }
    if (!isVariantSelectionComplete) {
      toast.error(locale === "id" ? "Pilih model, warna, dan ukuran terlebih dahulu" : "Please select style, color, and size first");
      return;
    }
    navigate("/checkout", {
      state: {
        buy_now: true,
        product_id: product.id,
        product_name: product.name,
        brand_name: product.brand_name || product.brand?.name,
        product_image: product.images?.[0] || "",
        product_price: displayPricing.final_price,
        product_base_price: displayPricing.base_price,
        discount_pct: displayPricing.discount_pct,
        savings_per_item: displayPricing.savings,
        stock_type: product.stock_type,
        variants: product.variants || [],
        weight_gram: product.weight_gram || product.weight || product.berat || "",
        variant: { variant_id: selectedVariant?.variant_id, style: selectedStyle || undefined, size: selectedSize, color: selectedColor, size_price_adjustment: displayPricing.size_price_adjustment || selectedVariant?.size_price_adjustment || 0 },
        quantity,
      }
    });
  };

  const handleWishlistToggle = async () => {
    if (!user) { navigate("/login"); return; }
    try {
      const { data } = await API.post("/wishlist/toggle", { product_id: product.id });
      setIsWishlisted(data.wishlisted);
      toast.success(data.wishlisted ? (locale === "id" ? "Ditambahkan ke wishlist" : "Added to wishlist") : (locale === "id" ? "Dihapus dari wishlist" : "Removed from wishlist"));
    } catch { toast.error("Failed"); }
  };


  const handleCopyProductShare = async () => {
    if (!product) return;
    const text = buildProductShareText(product, brandLabel);
    try {
      await navigator.clipboard.writeText(text);
      toast.success(locale === "id" ? "Detail produk berhasil disalin" : "Product details copied");
    } catch {
      toast.error(locale === "id" ? "Gagal menyalin detail produk" : "Failed to copy product details");
    }
  };

  if (loading) return <div className="min-h-screen"><Header /><div className="flex items-center justify-center py-32"><div className="animate-pulse text-stone-400">{t("product_detail.loading")}</div></div></div>;
  if (!product) return null;

  return (
    <div className="min-h-screen" data-testid="product-detail-page">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Gallery */}
          <div>
            <div
              className="relative group aspect-[3/4] bg-stone-100 mb-4 overflow-hidden rounded-2xl border border-stone-100 shadow-sm touch-pan-y select-none"
              onTouchStart={handleImageTouchStart}
              onTouchEnd={handleImageTouchEnd}
              data-testid="product-main-image-swipe-area"
              aria-label={imageCount > 1 ? "Geser kanan atau kiri untuk melihat gambar produk lainnya" : undefined}
            >
              <img src={mainImageSrc} alt={product.name} className="w-full h-full object-cover" draggable="false" data-testid="product-main-image" />
              {imageCount > 1 && (
                <>
                  <button
                    type="button"
                    onClick={showPreviousImage}
                    className="absolute left-3 top-1/2 -translate-y-1/2 h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-white/65 text-stone-700 shadow-sm backdrop-blur-sm border border-white/60 flex items-center justify-center opacity-75 sm:opacity-0 sm:group-hover:opacity-90 hover:opacity-100 hover:bg-white/85 transition-all"
                    aria-label="Gambar sebelumnya"
                    data-testid="product-main-image-prev"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    type="button"
                    onClick={showNextImage}
                    className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-white/65 text-stone-700 shadow-sm backdrop-blur-sm border border-white/60 flex items-center justify-center opacity-75 sm:opacity-0 sm:group-hover:opacity-90 hover:opacity-100 hover:bg-white/85 transition-all"
                    aria-label="Gambar berikutnya"
                    data-testid="product-main-image-next"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/35 text-white text-[11px] px-3 py-1 backdrop-blur-sm opacity-80 sm:opacity-0 sm:group-hover:opacity-80 transition-opacity">
                    {selectedImage + 1} / {imageCount}
                  </div>
                </>
              )}
            </div>
            {imageCount > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-stone-300 scrollbar-track-transparent" data-testid="product-thumbnail-strip">
                {productImages.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedImage(i)}
                    className={`shrink-0 w-20 h-24 sm:w-24 sm:h-28 bg-stone-100 overflow-hidden rounded-xl border-2 transition-all snap-start ${i === selectedImage ? "border-primary shadow-sm" : "border-transparent hover:border-stone-200"}`}
                    data-testid={`product-thumbnail-${i}`}
                  >
                    <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="space-y-6">
            <div>
              {product.stock_type === "preorder" ? (
                product.is_po_open_calculated !== 1 ? (
                  <Badge className="bg-red-50 text-red-700 hover:bg-red-50 text-[10px] uppercase tracking-wider font-bold mb-3" data-testid="detail-badge-po-closed">{t("admin_products.po_closed")}</Badge>
                ) : (
                  <Badge className="bg-amber-50 text-amber-700 hover:bg-amber-50 text-[10px] uppercase tracking-wider font-semibold mb-3" data-testid="detail-badge-preorder">PreOrder</Badge>
                )
              ) : (
                <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 text-[10px] uppercase tracking-wider font-semibold mb-3" data-testid="detail-badge-ready">{locale === "id" ? "Ready Stock" : "Ready Stock"}</Badge>
              )}
              {product.brand_id ? (
                <Link to={`/brands/${product.brand_id}`} className="hover:text-primary transition-colors">
                  <p className="font-sans text-xs uppercase tracking-[0.2em] text-stone-400 mb-2">
                    {brandLabel}
                  </p>
                </Link>
              ) : (
                <p className="font-sans text-xs uppercase tracking-[0.2em] text-stone-400 mb-2">
                  No Brand
                </p>
              )}
              <div className="flex items-start gap-3 mb-4">
                <h1 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-stone-900 flex-1" data-testid="product-detail-name">
                  {product.name} by {brandLabel}
                </h1>
                {user?.role === "admin" && (
                  <button
                    type="button"
                    onClick={handleCopyProductShare}
                    className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-stone-200 bg-white px-3 py-2 text-xs font-medium text-stone-600 hover:border-primary hover:text-primary transition-colors"
                    data-testid="copy-product-share-btn"
                    aria-label={locale === "id" ? "Salin detail produk" : "Copy product details"}
                    title={locale === "id" ? "Salin detail produk" : "Copy product details"}
                  >
                    <Copy className="w-4 h-4" />
                    <span className="hidden sm:inline">Copy</span>
                  </button>
                )}
              </div>
            </div>

            {product.stock_type === "preorder" && (
              <div className={`p-4 space-y-2 border ${product.is_po_open_calculated !== 1 ? "bg-red-50 border-red-100" : "bg-amber-50 border-amber-100"}`} data-testid="preorder-info">
                <div className={`flex items-center gap-2 ${product.is_po_open_calculated !== 1 ? "text-red-700" : "text-amber-700"}`}>
                  <Clock className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    {product.is_po_open_calculated !== 1 ? t("admin_products.po_closed") : t("product_detail.preorder_info")}
                  </span>
                </div>
                {product.preorder_closing_date && <p className={`text-sm ${product.is_po_open_calculated !== 1 ? "text-red-600" : "text-amber-600"}`}>{t("product_detail.closing_date")}: {formatPoCloseWib(product.preorder_closing_date)}</p>}
                {product.preorder_delivery_estimate && <p className={`text-sm ${product.is_po_open_calculated !== 1 ? "text-red-600" : "text-amber-600"}`}>{t("product_detail.delivery_est")}: {product.preorder_delivery_estimate}</p>}
                {product.is_po_open_calculated !== 1 && (
                  <p className="text-xs font-bold text-red-600 mt-2">
                    {locale === "id" ? "Maaf, pendaftaran Pre-Order untuk produk ini telah ditutup." : "Sorry, Pre-Order registration for this product is closed."}
                  </p>
                )}
              </div>
            )}

            <div className="flex items-baseline gap-3 flex-wrap">
              {displayPricing.savings > 0 ? (
                <>
                  <span className="text-lg text-stone-400 line-through">{formatPrice(displayPricing.base_price)}</span>
                  <span className="text-2xl font-semibold text-stone-900" data-testid="product-detail-price">{formatPrice(displayPricing.final_price)}</span>
                  <span className={`text-xs font-bold px-2 py-1 rounded ${displayPricing.campaign_applied ? "text-purple-700 bg-purple-50" : "text-red-600 bg-red-50"}`}>
                    {displayPricing.campaign_applied ? `Campaign ${displayDiscountLabel}` : displayDiscountLabel}
                  </span>
                  <span className="text-xs text-emerald-600 font-medium">{locale === "id" ? "Hemat" : "Save"} {formatPrice(displayPricing.savings)}</span>
                </>
              ) : (
                <span className="text-2xl font-semibold text-stone-900" data-testid="product-detail-price">{formatPrice(displayPricing.final_price)}</span>
              )}
            </div>
            {displayPricing.campaign_applied && (
              <p className="text-xs text-purple-700 font-medium -mt-1">
                {locale === "id" ? "Campaign aktif" : "Active campaign"}: {displayPricing.campaign?.campaign_name || "Campaign"}
                {displayDiscountLabel ? ` · ${locale === "id" ? "Diskon" : "Discount"}: ${displayDiscountLabel}` : ""}
                {displayPricing.normal_role_price ? ` · ${locale === "id" ? "Harga role normal" : "Normal role price"}: ${formatPrice(displayPricing.normal_role_price)}` : ""}
              </p>
            )}

            {displaySizeAdjustment > 0 && (
              <p className="text-xs text-stone-500 -mt-1">
                {locale === "id" ? "Termasuk tambahan size" : "Includes size adjustment"}: <span className="font-semibold text-stone-700">+{formatPrice(displaySizeAdjustment)}</span>
                {displayPricing.discounted_base_price ? (
                  <span className="text-stone-400"> · {locale === "id" ? "setelah diskon" : "after discount"}: {formatPrice(displayPricing.discounted_base_price)}</span>
                ) : null}
              </p>
            )}

            {/* Style Selection (only if product has styles) */}
            {hasStyles && styles.length > 0 && (
              <div>
                <p className="font-sans text-xs uppercase tracking-[0.15em] text-stone-500 font-semibold mb-3">{t("admin_products.style") || "Style"}</p>
                <div className="flex flex-wrap gap-2">
                  {styles.map((s) => (
                    <button
                      key={s}
                      onClick={() => {
                        const nextSelection = getDefaultColorSizeForStyle(product, s);
                        setSelectedStyle(s);
                        setSelectedColor(nextSelection.color);
                        setSelectedSize(nextSelection.size);
                      }}
                      className={`px-4 py-2 text-sm border transition-colors ${selectedStyle === s ? "border-primary bg-primary/10 text-primary" : "border-stone-200 text-stone-600 hover:border-stone-300"}`}
                      data-testid={`style-btn-${s}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Color Selection */}
            {colors.length > 0 && (
              <div>
                <p className="font-sans text-xs uppercase tracking-[0.15em] text-stone-500 font-semibold mb-3">{t("product_detail.color")}</p>
                <div className="flex flex-wrap gap-2">
                  {colors.map((c) => (
                    <button
                      key={c}
                      onClick={() => {
                        setSelectedColor(c);
                        setSelectedSize(getDefaultSizeForColor(product, selectedStyle, c));
                      }}
                      className={`px-4 py-2 text-sm border transition-colors ${selectedColor === c ? "border-primary bg-primary/10 text-primary" : "border-stone-200 text-stone-600 hover:border-stone-300"}`}
                      data-testid={`color-btn-${c}`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Size Selection */}
            {sizes.length > 0 && (
              <div>
                <p className="font-sans text-xs uppercase tracking-[0.15em] text-stone-500 font-semibold mb-3">{t("product_detail.size")}</p>
                <div className="flex flex-wrap gap-2">
                  {sizes.map((s) => (
                    <button
                      key={s}
                      onClick={() => setSelectedSize(s)}
                      className={`px-4 py-2 text-sm border transition-colors ${selectedSize === s ? "border-primary bg-primary/10 text-primary" : "border-stone-200 text-stone-600 hover:border-stone-300"}`}
                      data-testid={`size-btn-${s}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div>
              <p className="font-sans text-xs uppercase tracking-[0.15em] text-stone-500 font-semibold mb-3">{t("product_detail.quantity")}</p>
              <div className="flex items-center gap-4">
                <button disabled={product.stock_type === "preorder" && product.is_po_open_calculated !== 1} onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-10 h-10 border border-stone-200 flex items-center justify-center hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="qty-minus">
                  <Minus className="w-4 h-4" />
                </button>
                <span className="text-lg font-medium w-8 text-center" data-testid="qty-value">{quantity}</span>
                <button disabled={product.stock_type === "preorder" && product.is_po_open_calculated !== 1} onClick={() => setQuantity(quantity + 1)} className="w-10 h-10 border border-stone-200 flex items-center justify-center hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="qty-plus">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              {selectedVariant && product.stock_type === "ready_stock" && (
                <p className="text-xs text-stone-400 mt-2"><Package className="w-3 h-3 inline mr-1" />{selectedVariant.stock} {t("product_detail.in_stock")}</p>
              )}
            </div>

            {/* Actions */}
            {product.stock_type === "preorder" ? (
              /* ── PO mode: local cart ── */
              <div className="space-y-4 pt-4">
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleAddToCart}
                    disabled={adding || product.is_po_open_calculated !== 1 || !isVariantSelectionComplete}
                    className="flex-1 bg-primary text-primary-foreground px-8 py-3.5 text-sm uppercase tracking-wider hover:opacity-90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:bg-stone-300 disabled:text-stone-600 disabled:cursor-not-allowed"
                    data-testid="add-to-po-cart-btn"
                  >
                    <ClipboardList className="w-4 h-4" />
                    {product.is_po_open_calculated !== 1 ? t("admin_products.po_closed") : (locale === "id" ? "Tambah ke List" : "Add to List")}
                  </button>
                  <button
                    onClick={handleWishlistToggle}
                    className={`w-12 h-12 border flex items-center justify-center transition-colors ${isWishlisted ? "border-red-300 bg-red-50" : "border-stone-200 hover:bg-stone-50"}`}
                    data-testid="detail-wishlist-btn"
                  >
                    <Heart className={`w-5 h-5 ${isWishlisted ? "fill-red-500 text-red-500" : "text-stone-500"}`} />
                  </button>
                </div>
                {!isVariantSelectionComplete && product.is_po_open_calculated === 1 && (
                  <p className="text-xs text-stone-500 -mt-1">
                    {locale === "id" ? "Pilih model, warna, dan ukuran terlebih dahulu." : "Please select style, color, and size first."}
                  </p>
                )}

                {/* PO Cart Panel */}
                {poCart.length > 0 && (
                  <div className="border border-amber-200 rounded-xl overflow-hidden bg-amber-50">
                    {/* Header toggle */}
                    <button
                      onClick={() => setShowPOCart(v => !v)}
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-amber-100 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <ClipboardList className="w-4 h-4 text-amber-600" />
                        <span className="text-sm font-semibold text-amber-800">
                          {locale === "id" ? "List Pre-Order Kamu" : "Your Pre-Order List"}
                        </span>
                        <span className="bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{poCart.length}</span>
                      </div>
                      {showPOCart ? <ChevronUp className="w-4 h-4 text-amber-600" /> : <ChevronDown className="w-4 h-4 text-amber-600" />}
                    </button>

                    {showPOCart && (
                      <div className="px-4 pb-4">
                        {/* Item rows */}
                        <div className="space-y-2 mb-3">
                          {poCart.map(item => {
                            const pricing = getPricingForVariant(item.style, item.color, item.size);
                            const unitPrice = pricing.final_price || 0;
                            const rowTotal = unitPrice * item.quantity;
                            const detailRows = [
                              { label: locale === "id" ? "Varian" : "Style", value: item.style },
                              { label: locale === "id" ? "Warna" : "Color", value: item.color },
                              { label: locale === "id" ? "Ukuran" : "Size", value: item.size },
                            ].filter(detail => detail.value);
                            return (
                              <div key={item.id} className="bg-white rounded-lg px-3 py-3 border border-amber-100 space-y-3">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex-1 min-w-0 space-y-2">
                                    {detailRows.length > 0 ? (
                                      <div className="space-y-1">
                                        {detailRows.map(detail => (
                                          <div key={`${item.id}-${detail.label}`} className="flex items-start gap-2 text-xs">
                                            <span className="w-14 shrink-0 text-stone-400">{detail.label}</span>
                                            <span className="text-stone-700 font-medium break-words">: {detail.value}</span>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <p className="text-xs font-medium text-stone-700">{locale === "id" ? "Tanpa varian" : "No variant"}</p>
                                    )}
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
                                      <span className="text-stone-500">{locale === "id" ? "Harga" : "Price"}: <span className="font-semibold text-stone-700">{formatPrice(unitPrice)}</span></span>
                                      <span className="text-stone-500">Subtotal: <span className="font-semibold text-stone-900">{formatPrice(rowTotal)}</span></span>
                                    </div>
                                  </div>
                                  <button onClick={() => handleRemovePOItem(item.id)} className="text-red-400 hover:text-red-600 transition-colors shrink-0" aria-label={locale === "id" ? "Hapus item" : "Remove item"}><Trash2 className="w-4 h-4" /></button>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                  <div className="flex items-center border border-stone-200 rounded overflow-hidden bg-white">
                                    <button onClick={() => handleUpdatePOQty(item.id, item.quantity - 1)} className="w-8 h-8 flex items-center justify-center text-stone-500 hover:bg-stone-50 text-xs"><Minus className="w-3 h-3" /></button>
                                    <span className="w-9 h-8 flex items-center justify-center text-xs font-semibold text-stone-800 border-x border-stone-200">{item.quantity}</span>
                                    <button onClick={() => handleUpdatePOQty(item.id, item.quantity + 1)} className="w-8 h-8 flex items-center justify-center text-stone-500 hover:bg-stone-50 text-xs"><Plus className="w-3 h-3" /></button>
                                  </div>
                                  <p className="text-sm font-semibold text-stone-900 whitespace-nowrap">{formatPrice(rowTotal)}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Grand total */}
                        <div className="flex justify-between items-center border-t border-amber-200 pt-3 mb-3">
                          <span className="text-sm font-medium text-stone-700">Total</span>
                          <span className="text-base font-bold text-stone-900">
                            {formatPrice(poCart.reduce((acc, i) => {
                              const pricing = getPricingForVariant(i.style, i.color, i.size);
                              return acc + (pricing.final_price || 0) * i.quantity;
                            }, 0))}
                          </span>
                        </div>

                        {/* Checkout button */}
                        <button
                          onClick={handlePOCheckout}
                          disabled={product.is_po_open_calculated === false}
                          className="w-full bg-primary text-primary-foreground py-3 text-sm uppercase tracking-wider hover:opacity-90 transition-colors font-semibold rounded-lg disabled:opacity-50 disabled:bg-stone-400 disabled:text-white"
                          data-testid="po-checkout-btn"
                        >
                          {product.is_po_open_calculated === false ? t("admin_products.po_closed") : (locale === "id" ? "Buat Pesanan PO" : "Place PO Order")}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              /* ── Ready Stock mode: normal buttons ── */
              <div className="space-y-2 pt-4">
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleAddToCart}
                    disabled={adding || !isVariantSelectionComplete}
                    className="flex-1 bg-primary text-primary-foreground px-8 py-3.5 text-sm uppercase tracking-wider hover:opacity-90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="add-to-cart-btn"
                  >
                    <ShoppingBag className="w-4 h-4" /> {t("product_detail.add_to_cart")}
                  </button>
                <button
                  onClick={handleBuyNow}
                  disabled={adding || !isVariantSelectionComplete}
                  className="flex-1 bg-secondary text-secondary-foreground px-8 py-3.5 text-sm uppercase tracking-wider hover:opacity-90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="buy-now-btn"
                >
                  <Zap className="w-4 h-4" /> {t("product_detail.buy_now")}
                </button>
                  <button
                    onClick={handleWishlistToggle}
                    className={`w-12 h-12 border flex items-center justify-center transition-colors ${isWishlisted ? "border-red-300 bg-red-50" : "border-stone-200 hover:bg-stone-50"}`}
                    data-testid="detail-wishlist-btn"
                  >
                    <Heart className={`w-5 h-5 ${isWishlisted ? "fill-red-500 text-red-500" : "text-stone-500"}`} />
                  </button>
                </div>
                {!isVariantSelectionComplete && (
                  <p className="text-xs text-stone-500">
                    {locale === "id" ? "Pilih model, warna, dan ukuran terlebih dahulu." : "Please select style, color, and size first."}
                  </p>
                )}
              </div>
            )}

            {product.description && (
              <div className="pt-4 border-t border-stone-100" data-testid="product-description-section">
                <p className="font-sans text-xs uppercase tracking-[0.15em] text-stone-500 font-semibold mb-3">
                  {locale === "id" ? "Deskripsi Produk" : "Product Description"}
                </p>
                <p className="font-sans text-sm text-stone-600 leading-relaxed whitespace-pre-line">{product.description}</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
